import type {HistoryDomainAdapterRegistry} from '../domain';
import {
    type DiscardHistoryTransactionReason,
    type DiscardHistoryTransactions,
    type ApplyHistoryTransaction,
    isChangeSetEmpty,
} from './ModelHistoryManager';
import type {
    CadTransaction,
    HistoryApplyDirection,
} from '../types';
import {
    commitHistoryTransactionWithProtocol,
    createHistoryChecksum,
    type HistoryPersistence,
    type HistoryPersistenceTransactionRecord,
} from '../persistence';

/**
 * 会话模型历史引用。
 */
interface SessionHistoryTransactionRef {
    /** 文档 ID。 */
    readonly documentId: string;
    /** 事务 ID。 */
    readonly transactionId: string;
    /** 事务序号。 */
    readonly seq: number;
}

/**
 * 会话模型历史管理器配置。
 */
export interface SessionModelHistoryManagerOptions {
    /** 持久化端口；实现方可以是 IndexedDB、内存测试桩或其他浏览器临时存储。 */
    readonly persistence: HistoryPersistence;
    /** adapter 注册表；用于判断空事务语义。 */
    readonly adapterRegistry?: HistoryDomainAdapterRegistry;
    /** 事务应用函数；undo / redo 时先调用，成功后才移动光标。 */
    readonly applyTransaction?: ApplyHistoryTransaction;
    /** 事务被永久丢弃时释放运行时快照。 */
    readonly onDiscardTransactions?: DiscardHistoryTransactions;
    /** 内存热窗口容量；默认保留最近 10 条事务。 */
    readonly hotTransactionLimit?: number;
    /** 当前时间函数，测试时可注入。 */
    readonly now?: () => number;
}

/**
 * 会话模型历史状态。
 */
export interface SessionModelHistoryState {
    /** 当前内存热窗口中的 undo 事务。 */
    readonly undoStack: readonly CadTransaction[];
    /** 当前内存热窗口中的 redo 事务。 */
    readonly redoStack: readonly CadTransaction[];
    /** undo 栈总深度，包含冷存储中的事务。 */
    readonly undoDepth: number;
    /** redo 栈总深度，包含冷存储中的事务。 */
    readonly redoDepth: number;
    /** 当前内存热事务数量。 */
    readonly hotTransactionCount: number;
    /** 内存热窗口容量。 */
    readonly hotTransactionLimit: number;
    /** 是否可 undo。 */
    readonly canUndo: boolean;
    /** 是否可 redo。 */
    readonly canRedo: boolean;
}

/**
 * 会话模型历史管理器。
 */
export interface SessionModelHistoryManager {
    /**
     * 提交模型事务。
     *
     * @param transaction 待提交事务。
     * @returns 实际入栈事务；空事务返回 null。
     */
    readonly commit: (transaction: CadTransaction) => Promise<CadTransaction | null>;
    /**
     * 撤销最近模型事务。
     *
     * @returns 被撤销事务；无事务时返回 null。
     */
    readonly undo: () => Promise<CadTransaction | null>;
    /**
     * 重做最近撤销事务。
     *
     * @returns 被重做事务；无事务时返回 null。
     */
    readonly redo: () => Promise<CadTransaction | null>;
    /**
     * 清空当前会话历史。
     */
    readonly clear: () => Promise<void>;
    /**
     * 只清空内存结构；用于页面卸载时配合外层存储目录清理。
     */
    readonly clearMemory: () => void;
    /**
     * 读取历史状态。
     *
     * @returns 历史状态。
     */
    readonly getState: () => SessionModelHistoryState;
}

/**
 * 创建事务引用。
 *
 * @param transaction CAD 事务。
 * @returns 事务引用。
 */
function createTransactionRef(transaction: CadTransaction): SessionHistoryTransactionRef {
    return {
        documentId: transaction.documentId,
        transactionId: transaction.id,
        seq: transaction.seq,
    };
}

/**
 * 创建事务热缓存 key。
 *
 * @param ref 事务引用。
 * @returns 热缓存 key。
 */
function createTransactionCacheKey(ref: Pick<SessionHistoryTransactionRef, 'documentId' | 'transactionId'>): string {
    return `${ref.documentId}:${ref.transactionId}`;
}

/**
 * 判断 committed 记录 checksum 是否有效。
 *
 * @param record 持久化事务记录。
 * @returns checksum 是否有效。
 */
function isCommittedRecordChecksumValid(record: HistoryPersistenceTransactionRecord): boolean {
    const checksum = createHistoryChecksum({
        transaction: record.transaction,
        blobRefs: record.blobRefs,
        checkpointId: record.checkpointId,
    });

    return record.checksum === checksum;
}

/**
 * 从引用列表中删除某个事务引用。
 *
 * @param refs 引用列表。
 * @param target 目标引用。
 */
function removeTransactionRef(refs: SessionHistoryTransactionRef[], target: SessionHistoryTransactionRef): void {
    const index = refs.findIndex((ref) => ref.documentId === target.documentId
        && ref.transactionId === target.transactionId);

    if (index >= 0) {
        refs.splice(index, 1);
    }
}

/**
 * 创建会话级模型历史管理器。
 *
 * 设计说明：
 * 1. cad-core 只依赖 HistoryPersistence 抽象，不依赖 web-client；
 * 2. 内存只保留最近 N 条热事务，默认 N=10；
 * 3. 所有 committed 事务都写入会话级持久层，Undo/Redo 目标不在热窗口时按需换入；
 * 4. 页面关闭或刷新时由宿主调用 clear/clearMemory 并清理持久层，因此不会跨会话恢复历史。
 *
 * @param options 管理器配置。
 * @returns 会话模型历史管理器。
 */
export function createSessionModelHistoryManager(
    options: SessionModelHistoryManagerOptions,
): SessionModelHistoryManager {
    const hotTransactionLimit = Math.max(1, options.hotTransactionLimit ?? 10);
    const applyTransaction = options.applyTransaction ?? (() => undefined);
    const undoRefs: SessionHistoryTransactionRef[] = [];
    const redoRefs: SessionHistoryTransactionRef[] = [];
    const hotTransactions = new Map<string, CadTransaction>();
    const hotTransactionKeys: string[] = [];

    const touchHotTransaction = (transaction: CadTransaction): void => {
        const ref = createTransactionRef(transaction);
        const key = createTransactionCacheKey(ref);
        const existingIndex = hotTransactionKeys.indexOf(key);

        if (existingIndex >= 0) {
            hotTransactionKeys.splice(existingIndex, 1);
        }

        hotTransactions.set(key, transaction);
        hotTransactionKeys.push(key);

        while (hotTransactionKeys.length > hotTransactionLimit) {
            const coldKey = hotTransactionKeys.shift();
            if (coldKey) {
                hotTransactions.delete(coldKey);
            }
        }
    };

    const deleteHotTransaction = (ref: SessionHistoryTransactionRef): void => {
        const key = createTransactionCacheKey(ref);
        const index = hotTransactionKeys.indexOf(key);

        if (index >= 0) {
            hotTransactionKeys.splice(index, 1);
        }
        hotTransactions.delete(key);
    };

    const loadTransaction = async (ref: SessionHistoryTransactionRef): Promise<CadTransaction> => {
        const key = createTransactionCacheKey(ref);
        const hotTransaction = hotTransactions.get(key);

        if (hotTransaction) {
            touchHotTransaction(hotTransaction);

            return hotTransaction;
        }

        const records = await options.persistence.loadCommittedTransactions({
            documentId: ref.documentId,
            afterSeq: ref.seq - 1,
            untilSeq: ref.seq,
        });
        const record = records.find((item) => item.transactionId === ref.transactionId);

        if (!record) {
            throw new Error(`无法从历史冷存储读取事务：${ref.documentId}/${ref.transactionId}`);
        }

        if (!isCommittedRecordChecksumValid(record)) {
            throw new Error(`历史事务 checksum 校验失败：${ref.documentId}/${ref.transactionId}`);
        }

        touchHotTransaction(record.transaction);

        return record.transaction;
    };

    const updateCursor = async (documentId: string): Promise<void> => {
        const undoCursorSeq = undoRefs[undoRefs.length - 1]?.seq ?? 0;
        const currentSeq = Math.max(
            0,
            ...undoRefs.map((ref) => ref.seq),
            ...redoRefs.map((ref) => ref.seq),
        );

        await options.persistence.updateDocumentCursor({
            documentId,
            currentSeq,
            undoCursorSeq,
            updatedAt: options.now?.() ?? Date.now(),
        });
    };

    const notifyDiscardTransactions = (
        transactions: readonly CadTransaction[],
        reason: DiscardHistoryTransactionReason,
    ): void => {
        if (transactions.length === 0) {
            return;
        }
        options.onDiscardTransactions?.(transactions, reason);
    };

    const discardRefs = async (
        refs: readonly SessionHistoryTransactionRef[],
        reason: DiscardHistoryTransactionReason,
    ): Promise<void> => {
        if (refs.length === 0) {
            return;
        }

        const transactions: CadTransaction[] = [];
        const refsByDocument = new Map<string, SessionHistoryTransactionRef[]>();

        for (const ref of refs) {
            transactions.push(await loadTransaction(ref));
            const group = refsByDocument.get(ref.documentId) ?? [];

            group.push(ref);
            refsByDocument.set(ref.documentId, group);
        }

        for (const [documentId, documentRefs] of refsByDocument) {
            const transactionIds = documentRefs.map((ref) => ref.transactionId);
            const updatedAt = options.now?.() ?? Date.now();

            if (options.persistence.markTransactionsDiscarded) {
                await options.persistence.markTransactionsDiscarded({
                    documentId,
                    transactionIds,
                    updatedAt,
                });
            } else {
                for (const transactionId of transactionIds) {
                    await options.persistence.markTransactionDiscarded(documentId, transactionId, updatedAt);
                }
            }
        }

        refs.forEach((ref) => {
            deleteHotTransaction(ref);
            removeTransactionRef(undoRefs, ref);
            removeTransactionRef(redoRefs, ref);
        });
        notifyDiscardTransactions(transactions, reason);
    };

    /**
     * 回滚已经写入 committed 的新事务。
     *
     * 设计说明：
     * 新模型操作提交时必须先保证新事务 journal 可落盘，再清理 redo 分支。
     * 如果清理 redo 分支失败，需要把刚写入的新事务标记为 discarded，并恢复原光标，
     * 避免出现“模型被外层回滚，但冷存储多出一条新历史”的分叉状态。
     *
     * @param transaction 需要回滚的已提交事务。
     * @param undoCursorSeq 回滚后应该恢复的 undo 光标序号。
     */
    const rollbackCommittedTransaction = async (
        transaction: CadTransaction,
        undoCursorSeq: number,
    ): Promise<void> => {
        const updatedAt = options.now?.() ?? Date.now();

        await options.persistence.markTransactionDiscarded(transaction.documentId, transaction.id, updatedAt);
        await options.persistence.updateDocumentCursor({
            documentId: transaction.documentId,
            currentSeq: Math.max(
                0,
                ...undoRefs.map((ref) => ref.seq),
                ...redoRefs.map((ref) => ref.seq),
            ),
            undoCursorSeq,
            updatedAt,
        });
    };

    /**
     * 将未知错误转换为可定位的文本。
     *
     * @param error 未知错误。
     * @returns 错误文本。
     */
    const createErrorMessage = (error: unknown): string => {
        return error instanceof Error ? error.message : String(error);
    };

    /**
     * 应用历史事务并在光标写入失败时反向恢复。
     *
     * 设计说明：
     * Undo / Redo 必须先修改模型主状态，再移动历史光标；但如果光标写入失败，
     * 内存栈、场景和冷存储会分叉。这里在核心层统一做反向恢复，让宿主 UI 不需要
     * 针对每个领域单独编写补偿逻辑。
     *
     * @param ref 当前事务引用。
     * @param transaction 当前事务。
     * @param direction 应用方向。
     * @param moveRefs 正向移动 undo / redo 引用。
     * @param restoreRefs 光标失败时恢复 undo / redo 引用。
     */
    const applyTransactionWithCursorRollback = async (
        ref: SessionHistoryTransactionRef,
        transaction: CadTransaction,
        direction: HistoryApplyDirection,
        moveRefs: () => void,
        restoreRefs: () => void,
    ): Promise<void> => {
        const reverseDirection: HistoryApplyDirection = direction === 'undo' ? 'redo' : 'undo';

        applyTransaction(transaction, direction);
        moveRefs();

        try {
            await updateCursor(ref.documentId);
        } catch (error) {
            restoreRefs();

            try {
                applyTransaction(transaction, reverseDirection);
            } catch (rollbackError) {
                throw new Error(
                    `历史光标写入失败且反向恢复失败：cursor=${createErrorMessage(error)}；rollback=${createErrorMessage(rollbackError)}`,
                );
            }

            throw error;
        }
    };

    const createHotStackSnapshot = (refs: readonly SessionHistoryTransactionRef[]): readonly CadTransaction[] => {
        return refs
            .map((ref) => hotTransactions.get(createTransactionCacheKey(ref)))
            .filter((transaction): transaction is CadTransaction => Boolean(transaction));
    };

    return {
        async commit(transaction) {
            const committedTransaction: CadTransaction = {
                ...transaction,
                status: 'committed',
            };

            if (isChangeSetEmpty(committedTransaction.changeSet, options.adapterRegistry)) {
                return null;
            }

            const undoCursorSeqBeforeCommit = undoRefs[undoRefs.length - 1]?.seq ?? 0;
            const redoRefsBeforeCommit = [...redoRefs];

            await commitHistoryTransactionWithProtocol({
                persistence: options.persistence,
                transaction: committedTransaction,
                undoCursorSeq: committedTransaction.seq,
                now: options.now,
            });

            try {
                await discardRefs(redoRefsBeforeCommit, 'redo-branch-cleared');
            } catch (error) {
                await rollbackCommittedTransaction(committedTransaction, undoCursorSeqBeforeCommit);
                throw error;
            }

            undoRefs.push(createTransactionRef(committedTransaction));
            redoRefs.length = 0;
            touchHotTransaction(committedTransaction);

            return committedTransaction;
        },
        async undo() {
            const ref = undoRefs[undoRefs.length - 1];

            if (!ref) {
                return null;
            }

            const transaction = await loadTransaction(ref);

            await applyTransactionWithCursorRollback(
                ref,
                transaction,
                'undo',
                () => {
                    undoRefs.pop();
                    redoRefs.push(ref);
                },
                () => {
                    redoRefs.pop();
                    undoRefs.push(ref);
                },
            );

            return transaction;
        },
        async redo() {
            const ref = redoRefs[redoRefs.length - 1];

            if (!ref) {
                return null;
            }

            const transaction = await loadTransaction(ref);

            await applyTransactionWithCursorRollback(
                ref,
                transaction,
                'redo',
                () => {
                    redoRefs.pop();
                    undoRefs.push(ref);
                },
                () => {
                    undoRefs.pop();
                    redoRefs.push(ref);
                },
            );

            return transaction;
        },
        async clear() {
            const refs = [...undoRefs, ...redoRefs];

            await discardRefs(refs, 'history-cleared');
            undoRefs.length = 0;
            redoRefs.length = 0;
            hotTransactions.clear();
            hotTransactionKeys.length = 0;
        },
        clearMemory() {
            undoRefs.length = 0;
            redoRefs.length = 0;
            hotTransactions.clear();
            hotTransactionKeys.length = 0;
        },
        getState() {
            return {
                undoStack: createHotStackSnapshot(undoRefs),
                redoStack: createHotStackSnapshot(redoRefs),
                undoDepth: undoRefs.length,
                redoDepth: redoRefs.length,
                hotTransactionCount: hotTransactions.size,
                hotTransactionLimit,
                canUndo: undoRefs.length > 0,
                canRedo: redoRefs.length > 0,
            };
        },
    };
}
